import { env } from "@/env";
import { localizeHref } from "@/paraglide/runtime";
import { toast } from "sonner";
import { getRegion } from "./auth/store";

export interface BaseResponse<T> {
	code: number;
	data: T;
	msg: string;
}

export const SERVER_ERROR_CODE = {
	SESSION_EXPIRED: 4200,
	INSUFFICIENT_BALANCE: 13003,
	DISABLED_USER: 11001,
	INVALID_REGION: 11002,
} as const;

let currentError: string | null = null; // 跟踪当前显示的错误消息
let lastToastTime = 0; // 记录最后一次 toast 时间，用于防抖
const TOAST_DEBOUNCE_MS = 3500; // 防抖时间间隔（毫秒）

export async function baseRequest<T = any>(
	endpoint: string,
	options: RequestInit,
	showErrorToast = true,
): Promise<BaseResponse<T>> {
	const authToken = localStorage.getItem("AUTH_TOKEN");
	const headers = {
		"X-APP-ID": "AIFK",
		"X-REGION": getRegion(),
		Authorization: authToken || "",
		...(options.headers || {}),
	};

	try {
		const response = await fetch(`${env.VITE_API_HOST}${endpoint}`, {
			...options,
			headers,
		});
		const rt = await response.json();

		if (rt.code !== 0) {
			if (rt.code === SERVER_ERROR_CODE.SESSION_EXPIRED) {
				setTimeout(() => {
					localStorage.removeItem("AUTH_TOKEN");
					window.location.href = localizeHref("/");
				}, 200);
			}
			if (rt.code === SERVER_ERROR_CODE.DISABLED_USER) {
				setTimeout(() => {
					localStorage.removeItem("AUTH_TOKEN");
					window.location.href = localizeHref("/");
				}, 200);
			}
			if (rt.code === SERVER_ERROR_CODE.INVALID_REGION) {
				setTimeout(() => {
					window.location.href = localizeHref("/");
				}, 200);
			}
			const errorMessage = rt?.msg || "Unknown error.";
			const now = Date.now();

			// 如果是同样的错误，直接跳过
			if (currentError === errorMessage) {
				return rt as BaseResponse<T>;
			}

			// 如果是新的错误且在防抖时间内，也跳过
			if (now - lastToastTime < TOAST_DEBOUNCE_MS) {
				return rt as BaseResponse<T>;
			}

			// 显示新错误
			if (showErrorToast) {
				toast.dismiss();
				toast.error(errorMessage);
			}
			currentError = errorMessage;
			lastToastTime = now;
		} else {
			currentError = null;
		}
		return rt as BaseResponse<T>;
	} catch (err) {
		const errorMessage = err instanceof Error ? err.message : "Unknown error.";
		const now = Date.now();

		// 如果是同样的错误，直接跳过
		if (currentError === errorMessage) {
			return {
				code: -1,
				msg: errorMessage,
				data: null,
			} as BaseResponse<T>;
		}

		// 如果是新的错误且在防抖时间内，也跳过
		if (now - lastToastTime < TOAST_DEBOUNCE_MS) {
			return {
				code: -1,
				msg: errorMessage,
				data: null,
			} as BaseResponse<T>;
		}

		// 显示新错误
		if (showErrorToast) {
			toast.dismiss();
			toast.error(errorMessage);
		}
		currentError = errorMessage;
		lastToastTime = now;
		return {
			code: -1,
			msg: errorMessage,
			data: null,
		} as BaseResponse<T>;
	}
}

export const request = {
	get: (
		endpoint: string,
		data?: Record<string, any>,
		options: RequestInit = {},
		showErrorToast = true,
	) =>
		baseRequest(
			endpoint + (data ? `?${new URLSearchParams(data).toString()}` : ""),
			{ ...options, method: "GET" },
			showErrorToast,
		),
	post: (
		endpoint: string,
		data?: any,
		options: RequestInit = {},
		showErrorToast = true,
	) => {
		let body: any;
		let headers = options.headers || {};
		if (data instanceof FormData) {
			body = data;
			headers = { ...headers };
		} else {
			body = JSON.stringify(data);
			headers = { "Content-Type": "application/json", ...headers };
		}
		return baseRequest(
			endpoint,
			{
				...options,
				method: "POST",
				body,
				headers,
			},
			showErrorToast,
		);
	},
};

export async function tryResponse<T>(
	res: Promise<BaseResponse<T>>,
): Promise<T> {
	const dt = await res;
	if (dt.code === 0) {
		return dt.data as T;
	}
	throw new Error(dt.msg);
}
